<template>
    <div id="member-login">
        <!-- Feature Flag Inactive - 403 Page -->
        <div v-if="!featureFlagActive" class="feature-flag-disabled">
            <div class="feature-flag-disabled__content">
                <div class="feature-flag-disabled__icon">403</div>
                <h2 class="feature-flag-disabled__title">Page Not Available</h2>
                <p class="feature-flag-disabled__message">This page is currently not available. Please contact the administrator to activate this page.</p>
            </div>
        </div>
        <div class="d-flex justify-content-center spinner-border text-primary spinner-position form" v-if="featureFlagActive && loading"></div>
        <div class="spinner" v-if='featureFlagActive && loading'></div>
        <div class="login-wrapper" :style="backgroundStyle" v-if="featureFlagActive && !loading">
            <div class="glass-login-card">
                <div class="logo-brand mb-4">
                    <img v-if="!merchant_id" id="logo-brand-omnih" :src="setCorporateLogo" alt="logo">
                    <img v-else id="logo-brand" :src="branchLogo" alt="logo">
                </div>
                
                <label v-if="registerState" style="color: red; text-align: center">
                    {{ registerMessage ? 'Successfully created an account and can log in using the new registered email password' : 'Please wait for merchant verify your account request' }}
                </label>
                
                <form @submit.prevent="submitLogin" id="form-login" class="form-login" v-if="!isOtpMode">
                    <div class="custom-input-group mb-3">
                        <i class="fas fa-envelope input-icon"></i>
                        <input name="email" v-model="email" type="email" required placeholder="Email" class="glass-input"/>
                    </div>

                    <div class="custom-input-group mb-3 position-relative">
                        <i class="fas fa-lock input-icon"></i>
                        <input name="password" v-model="password" :type="showPassword ? 'text' : 'password'" required placeholder="Password" class="glass-input" style="padding-right: 40px;"/>
                        <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'" @click="showPassword = !showPassword"
                            style="position: absolute; right: 15px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6c757d;"></i>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mb-4 mt-1">
                        <a @click="goToForgotPassword" class="text-link" style="cursor: pointer;">
                            <small>Forgot password?</small>
                        </a>
                        <a href="javascript:void(0);" class="text-link" @click="switchToOtpMode">
                            <small>Login with OTP</small>
                        </a>
                    </div>

                    <div v-if="validation" class="custom-alert mb-3">
                        <div class="alert-content">
                            <span class="alert-icon">&#9888;</span>
                            <p>{{ passwordErrorMessage || 'Invalid email or password' }}</p>
                        </div>
                    </div>

                    <div v-if="validationEntity" class="custom-alert mb-3">
                        <div class="alert-content">
                            <span class="alert-icon">&#9888;</span>
                            <p>Invalid entity</p>
                        </div>
                    </div>

                    <div v-if="validationMemberOnly" class="custom-alert mb-3">
                        <div class="alert-content">
                            <span class="alert-icon">&#9888;</span>
                            <p>Invalid level, please login with member only</p>
                        </div>
                    </div>

                    <div class="custom-alert mb-3" v-if="otpErrorMessage">{{ otpErrorMessage }}</div>

                    <div class="d-flex justify-content-center w-100">
                        <button type="button" class="login-button" v-if="authStatus === 'loading'" disabled>
                            Loading...
                        </button>
                        <button type="submit" class="login-button" v-if="authStatus !== 'loading'">
                            Login
                        </button>
                    </div>
                </form>

                <form @submit.prevent="submitOtp" id="form-otp" v-if="isOtpMode">
                    <div class="custom-input-group mb-3" v-if="!otpRequested">
                        <i class="fas fa-envelope input-icon"></i>
                        <input v-model="otpEmail" type="email" required placeholder="Email" class="glass-input"/>
                    </div>

                    <div v-if="!otpRequested" class="d-flex justify-content-center mt-3">
                        <button type="button" class="login-button" @click="requestOtp">Send OTP</button>
                    </div>

                    <div v-if="otpRequested" class="d-flex flex-column gap-3 mt-3">
                        <div class="mb-2">
                            <OInputDigit 
                                v-model="otp" 
                                placeholder="000000" 
                                :maxlength="6" 
                                class="modern-digit-input"
                            />
                        </div>

                        <div class="d-flex flex-column align-items-center gap-2">
                            <div v-if="showCountdown">
                                <span class="countdown-text" style="color: #333333">{{ formattedTime }}</span>
                            </div>
                            <button type="button" class="login-button resend-btn" v-if="!showCountdown" @click="resendOTP" style="background: transparent; border: 1px solid #DE5B53; color: #DE5B53;">Resend OTP</button>
                        </div>

                        <div class="d-flex justify-content-center">
                            <button type="submit" class="login-button">Submit OTP</button>
                        </div>
                        <div class="text-center mt-2">
                            <p class="custom-label" style="color: #6c757d; font-size: 12px;">
                                *Please check your <strong>{{ otpEmail }}</strong> mailbox to get the OTP code.
                            </p>
                        </div>
                    </div>
                    
                    <div v-if="otpErrorMessage" class="custom-alert text-center mt-2">
                        <span class="alert-icon">&#9888;</span>
                        <p>{{ otpErrorMessage }}</p>
                    </div>
                    
                    <div class="divider-with-text mt-4 mb-3" v-if="isOtpMode">
                        <span></span>
                    </div>

                    <div class="d-flex justify-content-center mt-1">
                        <a href="javascript:void(0);" class="text-link text-center" @click="switchToLoginMode">
                            <small>Back to Login</small>
                        </a>
                    </div>
                </form>

                <div class="divider-with-text mt-4 mb-4" v-if="!isOtpMode && !otpRequested">
                    <span>or</span>
                </div>

                <div class="d-flex flex-column align-items-center" v-if="!isOtpMode && !otpRequested">
                    <!-- Native: use Capacitor SocialLogin plugin -->
                    <GoogleLoginButton
                        v-if="isNativePlatform"
                        button-text="Login with Google"
                        @login-success="handleGoogleLoginSuccess"
                        @login-error="handleGoogleLoginError"
                    />
                    <AppleLoginButton
                        v-if="isIosPlatform"
                        button-text="Sign in with Apple"
                        @login-success="handleAppleLoginSuccess"
                        @login-error="handleAppleLoginError"
                    />
                    <!-- Web: use backend OAuth redirect -->
                    <button v-if="!isNativePlatform" type="button" class="google-login-button" @click="loginWithGoogle">
                        <img src="https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/Google__G__logo.svg.png" class="google-icon">
                        <span class="google-login-text">Login with Google</span>
                    </button>
                </div>

                <div class="d-flex justify-content-center mt-4" v-if="!isOtpMode && !otpRequested">
                    <span style="color: #6c757d; font-size: 14px;">Don't have an account?&nbsp;</span>
                    <a href="#" @click.prevent="goToSignUp" class="text-link" style="color: #DE5B53; font-weight: 600;">
                        Sign Up
                    </a>
                </div>

                <div class="terms-privacy-text text-center mt-3" v-if="!isOtpMode && !otpRequested">
                    <small class="text-policies" style="color: #808080; font-size: 11px;">
                        By logging in and signing up, you agree to our<br>
                        <a href="#" @click.prevent="openPrivacyPolicy" style="color: #DE5B53; text-decoration: none;">Privacy Policy</a> 
                        and 
                        <a href="#" @click.prevent="openTermsConditions" style="color: #DE5B53; text-decoration: none;">Terms & Conditions</a>
                    </small>
                </div>
            </div>
            
            <div class="login-footer">
                Copyright © PT. Omni Hotelier Internasional {{ currentYear }}
            </div>
        </div>
    </div>
    <o-modal 
        v-if="showPrivacyModal" 
        @close="closePrivacyModal"
        :modalClass="'show d-block policies-modal'"
        :bodyClass="'p-0'"
        :contentClass="'policies-modal-content'"
        :allow-html="false"
        :is-footer-visible="false"
        >
        <template #header>
            <h5 class="modal-title">Policies</h5>
        </template>
        <template #body>
            <div class="policies-container"
                style="height: 450px;
                        max-height: 450px;
                        min-height: 450px;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                        color: black;
                        word-wrap: break-word;
                        ">
                <!-- Tab Navigation -->
                <div class="policies-tabs">
                    <button 
                        class="tab-button" 
                        :class="{ active: activeTab === 'privacy' }"
                        @click="activeTab = 'privacy'">
                        Privacy Policy
                    </button>
                    <button 
                        class="tab-button" 
                        :class="{ active: activeTab === 'terms' }"
                        @click="activeTab = 'terms'">
                        Terms & Conditions
                    </button>
                </div>
                
                <!-- Tab Content -->
                <div class="tab-content" style="height: 450px; max-height: 450px; min-height: 450px; overflow-y: auto; padding: 1.5rem 1.5rem 2.5rem 1.5rem; flex: 1;">
                    <div v-if="activeTab === 'privacy'" class="policy-content">
                        <PublicPolicyDisplay type="privacy" />
                    </div>
                    
                    <div v-if="activeTab === 'terms'" class="policy-content">
                        <PublicPolicyDisplay type="terms" />
                    </div>
                </div>
            </div>
        </template>
        </o-modal>
    <TwoFactorVerify v-if="featureFlagActive" />
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import api from '@/helpers/api';
import { OModal, OInputDigit } from '@/components/OmnihControl';
import PublicPolicyDisplay from '@/components/PublicPolicyDisplay.vue';
import TwoFactorVerify from '@/components/two-factor/TwoFactorVerify.vue';
import GoogleLoginButton from '@/components/GoogleLoginButton.vue';
import AppleLoginButton from '@/components/AppleLoginButton.vue';
import storageService from '@/services/storageService';
import { pushNotificationHandler } from '@/mobile/plugins/pushNotification';
import { setSwipeBackEnabled } from '@/services/swipeBack';

export default {
    name: 'MemberLogin',
    components: {
        OModal,
        PublicPolicyDisplay,
        TwoFactorVerify,
        GoogleLoginButton,
        AppleLoginButton,
        OInputDigit,
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const route = useRoute();
        // State
        const email = ref('');
        const password = ref('');
        const loading = ref(true);
        const validation = ref(false);
        const validateLevel = ref(false);
        const validationEntity = ref(false);
        const merchant_id = ref("");
        const validationMemberOnly = ref(false);
        const registerState = ref(false);
        const registerMessage = ref(false);
        const showPassword = ref(false);
        const branchLogo = ref('');
        const autoLoginData = ref(null);
        const corporate_id = ref("");
        const dashboard = ref(false);
        const otp = ref('');
        const otpRequested = ref(false);
        const otpErrorMessage = ref('');
        const redirectUrl = ref('');
        const showCountdown = ref(false);
        const showRefreshButton = ref(false);
        const countdownMinutes = ref(2);
        const countdownSeconds = ref(0);
        const countdownInterval = ref(null);
        const isOtpMode = ref(false);
        const isProcessing = ref(false);
        const loginBackground = ref('');
        const corporateLogo = ref('');
        const passwordErrorMessage = ref('');
        const activities = ref(false);
        const otpEmail = ref('');
        const showPrivacyModal = ref(false);
        const activeTab = ref('privacy');
        const referral_code = ref('');
        const refParam = ref('');
        const featureFlagActive = ref(true);
        const isNativePlatform = computed(() => Capacitor.isNativePlatform());
        const isIosPlatform = computed(() => Capacitor.getPlatform() === 'ios');


        // Helper
        const _authCheckPromise = ref(null);
        const currentYear = new Date().getFullYear();

        // Vuex Getters
        const isLoggedIn = computed(() => store.getters['Auth/isLoggedIn']);
        const authStatus = computed(() => store.getters['Auth/authStatus']);

        // Computed
        const goToForgotPassword = () => {
            localStorage.setItem('auth_origin', 'member');
            if (merchant_id.value) {
                router.push({ name: 'ForgotPassword', query: { merchant_id: merchant_id.value } });
            } else {
                router.push({ name: 'ForgotPassword' });
            }
        };
        const signUpLink = computed(() => {
            const params = corporate_id.value && dashboard.value 
                ? { merchant_id: corporate_id.value } 
                : { merchant_id: merchant_id.value };
            
            const query = {};
            if (referral_code.value) {
                query.referral = referral_code.value;
            }
            
            return { 
                name: 'MemberSignUp', 
                params: params,
                query: Object.keys(query).length > 0 ? query : undefined
            };
        });
        const formattedTime = computed(() => {
            const minutes = countdownMinutes.value;
            const seconds = countdownSeconds.value < 10 ? `0${countdownSeconds.value}` : countdownSeconds.value;
            return `${minutes}:${seconds}`;
        });
        const setCorporateLogo = computed(() => corporateLogo.value);
        const backgroundStyle = computed(() => ({
            backgroundImage: `url(${loginBackground.value})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
        }));

        // Methods
        const setLoadingState = async (callback) => {
            if (isProcessing.value) return Promise.reject(new Error('Request already in progress'));
            loading.value = true;
            isProcessing.value = true;
            try {
                const result = await callback();
                return result;
            } finally {
                loading.value = false;
                isProcessing.value = false;
            }
        };

        function handleWebTempAccess(resp, resData) {
            if (window.opener === null) {
                const redirectUrlObj = new URL(resp.redirectUrl);
                redirectUrlObj.searchParams.append('username', resData.username);
                redirectUrlObj.searchParams.append('token', resData.token);
                redirectUrlObj.searchParams.append('tierId', resData.tierId);
                if (resData.tier_name) redirectUrlObj.searchParams.append('tier_name', resData.tier_name);
                if (resData.tier_icon) redirectUrlObj.searchParams.append('tier_icon', resData.tier_icon);
                if (resData.voucher) redirectUrlObj.searchParams.append('voucher', resData.voucher);
                if (resData.point) redirectUrlObj.searchParams.append('point', resData.point);
                if (resData.media) redirectUrlObj.searchParams.append('media', resData.media);
                window.location.href = redirectUrlObj;
            } else {
                window.opener.postMessage(resData, resp.redirectUrl);
                window.close();
            }
        }

        function handleRoleRedirect() {
            clearAutoLoginData();
            const storedRole = localStorage.getItem('role');
            const storedLevel = localStorage.getItem('level');
            if ((storedRole && storedRole !== 'member') && (storedLevel && storedLevel !== 'member')) {
                return;
            }
            // Check for intended URL from newsletter deep link (or other client routes)
            const redirectPath = localStorage.getItem('redirect_after_login');
            localStorage.removeItem('redirect_after_login');
            if (redirectPath && redirectPath.startsWith('/client/')) {
                router.replace(redirectPath);
                return;
            }
            router.replace({ name: 'client_dashboard' });
        }

        const autoLogin = async () => {
            if (!autoLoginData.value) {
                loading.value = false;
                return;
            }
            return setLoadingState(async () => {
                try {
                    const loginData = {
                        email: autoLoginData.value.email,
                        password: autoLoginData.value.password,
                        token: autoLoginData.value.token,
                        merchant_id: autoLoginData.value.merchant_id,
                        type: autoLoginData.value.type,
                        corporate_id: autoLoginData.value.corporate_id,
                        dashboard: autoLoginData.value.dashboard,
                        ref: autoLoginData.value.ref || refParam.value
                    };
                    const res = await store.dispatch('Auth/memberLogin', loginData);
                    localStorage.removeItem('autoLoginData');
                    if (res?.type === "web_temp_access" && res?.url !== null) {
                        const url = `${res.url}?member_token=${res.token}`;
                        const resData = {};
                        for (const key in res) {
                            if (Object.prototype.hasOwnProperty.call(res, key) && key !== 'url') {
                                resData[key] = res[key];
                                localStorage.setItem(key, JSON.stringify(res[key]));
                            }
                        }
                        let resp = null;
                        let response = null;
                        try {
                            response = await fetch(url);
                            resp = await response.json();
                        } catch (error) {
                            validation.value = true;
                            passwordErrorMessage.value = "Auto-login failed. Please try again manually.";
                            return;
                        }
                        if (!response || !response.ok) {
                            await handleFetchError(response, resp, logout);
                            return;
                        }
                        handleWebTempAccess(resp, resData);
                        return;
                    }
                    if (res?.type === "web_temp_access" && res?.url === null) {
                        validation.value = true;
                        return;
                    }
                    if (res.role === "member" || res.level === "member") {
                        handleRoleRedirect();
                        localStorage.removeItem('sso_ref_param');
                        return;
                    }
                    router.replace({ name: 'Home' });
                    localStorage.removeItem('sso_ref_param');
                } catch (err) {
                    if (err.response?.status === 429) {
                        validation.value = true;
                        passwordErrorMessage.value = "Rate limit exceeded. Please try again later.";
                    } else if (err.response?.data?.message === "AUTH.LOGIN.CORPORATE_ID_MISMATCH") {
                        validation.value = true;
                        passwordErrorMessage.value = "Please log in with account based merchant.";
                    } else {
                        validation.value = true;
                        passwordErrorMessage.value = "Auto-login failed. Please try again manually.";
                    }
                }
            });
        };

        const switchToOtpMode = () => {
            isOtpMode.value = true;
            otpRequested.value = false;
            otpEmail.value = '';
            otp.value = '';
            otpErrorMessage.value = '';
        };
        const switchToLoginMode = () => {
            isOtpMode.value = false;
            email.value = '';
            password.value = '';
            otpRequested.value = false;
            otpEmail.value = '';
            otp.value = '';
            showCountdown.value = false;
            clearInterval(countdownInterval.value);
        };
        const startCountdown = () => {
            showCountdown.value = true;
            showRefreshButton.value = false;
            countdownMinutes.value = 2;
            countdownSeconds.value = 0;
            clearInterval(countdownInterval.value);
            countdownInterval.value = setInterval(() => {
                if (countdownSeconds.value > 0) {
                    countdownSeconds.value--;
                    return;
                }
                if (countdownMinutes.value > 0) {
                    countdownMinutes.value--;
                    countdownSeconds.value = 59;
                    return;
                }
                clearInterval(countdownInterval.value);
                showCountdown.value = false;
                showRefreshButton.value = true;
            }, 1000);
        };
        function handleLoginErrorResponse(err) {
            if (!err.response) {
                validation.value = true;
                passwordErrorMessage.value = "Network error. Please try again later.";
                return;
            }
            const msg = err.response.data.message;
            const error = err.response.data.error;
            const errorMap = {
                "AUTH.LOGIN.INVALID.AUTH.FAIL.LEVEL": () => { validateLevel.value = true; },
                "AUTH.LOGIN.VALIDATION_ERROR": () => {
                    const parsedError = typeof error === "string" ? JSON.parse(error) : error;

                    if (error && error.includes('at least 6 characters')) {
                        if(parsedError.email && parsedError.password) {
                            validation.value = true;
                            passwordErrorMessage.value = `${parsedError.email[0]} and ${parsedError.password[0]}`;
                        } else {
                            validation.value = true;
                            passwordErrorMessage.value = parsedError.password[0];
                        }
                    }else if (error && error.includes('field is required')) {
                        if (parsedError.email && parsedError.password) {
                            validation.value = true; 
                            passwordErrorMessage.value = `${parsedError.email[0]} and ${parsedError.password[0]}`;
                        } 
                        else if (parsedError.email) {
                            validation.value = true;
                            passwordErrorMessage.value = parsedError.email[0];
                        } 
                        else if (parsedError.password) {
                            validation.value = true;
                            passwordErrorMessage.value = parsedError.password[0];
                        }
                    } else {
                        validation.value = true;
                        passwordErrorMessage.value = parsedError.email[0];
                    }
                },
                "AUTH.LOGIN.FAILED.UNKNOWN.ENTITY": () => { validation.value = false; validationEntity.value = true; },
                "AUTH.LOGIN.FAILED.MEMBER.ONLY": () => { validation.value = false; validationMemberOnly.value = true; },
                "AUTH.LOGIN.FAILED.MEMBER.NOT.YET.ACCEPTED": () => { validation.value = true; passwordErrorMessage.value = "Your account is still pending approval. Please wait for merchant verification."; },
                "AUTH.LOGIN.FAILED.MEMBER.LOGIN.WITH.SOCIALITE": () => { validation.value = true; passwordErrorMessage.value = "Please log in using your social media account."; },
                "AUTH.LOGIN.INVALID_EMAIL_PASSWORD.ERROR": () => { validation.value = true; passwordErrorMessage.value = "Invalid email or password account."; },
                "AUTH.LOGIN.CORPORATE_ID_MISMATCH": () => { validation.value = true; passwordErrorMessage.value = "Member ID does not match in at merchant"; }
            };
            if (errorMap[msg]) {
                errorMap[msg]();
            } else if (msg && msg.includes("No query results for model")) {
                validation.value = true;
                passwordErrorMessage.value = "Not Found";
            } else if (typeof msg === 'string' && msg === msg.toUpperCase()) {
                validation.value = true;
                try {
                    const parsedError = typeof error === 'string' ? JSON.parse(error) : error;
                    if (parsedError && typeof parsedError === 'object') {
                        const firstKey = Object.keys(parsedError)[0];
                        passwordErrorMessage.value = Array.isArray(parsedError[firstKey]) ? parsedError[firstKey][0] : parsedError[firstKey];
                    } else {
                        passwordErrorMessage.value = error;
                    }
                } catch (e) {
                    passwordErrorMessage.value = error;
                }
            } else {
                validation.value = true;
                passwordErrorMessage.value = msg;
            }
        }

        async function handleFetchError(response, resp, logoutFn) {
            const errorMessage = resp?.error || "An unexpected error occurred.";
            if (errorMessage === "Please contact the property to check the URL.") {
                await logoutFn(errorMessage);
            }
            validation.value = true;
            passwordErrorMessage.value = errorMessage;
        }

        async function processOtpResponse(res) {
            if (res.level !== "member") {
                // Non-member user: reject access on member portal
                await logout();
                otpErrorMessage.value = 'This login page is for members only. Please use the admin login page.';
                return true;
            }

            if (res?.type === "web_temp_access") {
                if (res?.url === null) {
                    throw new Error("Invalid reference parameter for Booking Engine integration.");
                }
                let url = `${res.url}?member_token=${res.token}`;
                if (activities.value) {
                    url = `${res.url}?member_token=${res.token}&activities=true`;
                }
                const resData = {};
                for (const key in res) {
                    if (Object.prototype.hasOwnProperty.call(res, key) && key !== 'url') {
                        resData[key] = res[key];
                        localStorage.setItem(key, JSON.stringify(res[key]));
                    }
                }
                let resp = null;
                let response = null;
                try {
                    response = await fetch(url);
                    resp = await response.json();
                } catch (error) {
                    validation.value = true;
                    passwordErrorMessage.value = "Auto-login failed. Please try again manually.";
                    return true;
                }
                if (!response || !response.ok) {
                    await handleFetchError(response, resp, logout);
                    return true;
                }
                handleWebTempAccess(resp, resData);
                return true;
            }

            handleRoleRedirect();
            localStorage.removeItem('sso_ref_param');
            return true;
        }

        const requestOtp = async () => {
            if (!otpEmail.value) {
                otpErrorMessage.value = 'Please enter a valid email to receive OTP.';
                return;
            }
            return setLoadingState(async () => {
                const data = {
                    email: otpEmail.value,
                    merchant_id: merchant_id.value ?? "",
                    corporate_id: corporate_id.value ?? "",
                    dashboard: dashboard.value ?? "",
                    otp_login: "login",
                    ref: refParam.value || undefined, // Add ref parameter for OTP
                };
                if (merchant_id.value || (corporate_id.value && dashboard.value)) {
                    data.type = 'web_temp_access';
                }
                try {
                    await store.dispatch('Member/request_otp', { data });
                    otpRequested.value = true;
                    otpErrorMessage.value = '';
                    startCountdown();
                } catch (error) {
                    if (error.response && error.response.data) {
                        const serverResponse = error.response.data;
                        otpErrorMessage.value = serverResponse.error || serverResponse.message || 'Failed to send OTP. Please try again.';
                    } else if (error.message) {
                        otpErrorMessage.value = error.message;
                    } else {
                        otpErrorMessage.value = 'Failed to send OTP. Please try again.';
                    }
                }
            });
        };

        const resendOTP = async () => {
            otpErrorMessage.value = '';
            await requestOtp();
        };

        const otpErrorMap = {
            "AUTH.LOGIN.INVALID.AUTH.FAIL.LEVEL": "Invalid level. Please log in with a valid member account.",
            "AUTH.LOGIN.CORPORATE_ID_MISMATCH": "Member ID does not match in at Corporate"
        };
        const submitOtp = async () => {
            if (!otp.value) {
                otpErrorMessage.value = 'Please enter the OTP code.';
                return;
            }
            otpErrorMessage.value = '';
            return setLoadingState(async () => {
                try {
                    await store.dispatch('Member/submit_otp', {
                        email: otpEmail.value,
                        otp: otp.value,
                        otp_login: "login",
                    });
                    const data = {
                        email: otpEmail.value,
                        merchant_id: merchant_id.value ?? "",
                        corporate_id: corporate_id.value ?? "",
                        dashboard: dashboard.value ?? "",
                        otp_login: "login",
                        ref: refParam.value || undefined, // Add ref parameter for check login
                    };
                    const res = await store.dispatch('Auth/checkLogin', data);
                    await processOtpResponse(res);
                } catch (err) {
                    const msg = err.response?.data?.message;
                    otpErrorMessage.value = otpErrorMap[msg] || "Failed to verify OTP.";
                }
            });
        };
        const login = async () => {
            return setLoadingState(async () => {
                const data = {
                    email: email.value,
                    password: password.value,
                    merchant_id: merchant_id.value ?? "",
                    corporate_id: corporate_id.value ?? "",
                    dashboard: dashboard.value ?? "",
                    otp_login: "login",
                    ref: refParam.value || undefined, // Add ref parameter
                };
                validation.value = false;
                validateLevel.value = false;
                validationEntity.value = false;
                validationMemberOnly.value = false;
                passwordErrorMessage.value = '';
                try {
                    const res = await store.dispatch('Auth/memberLogin', data);
                    
                    if (res?.requires_2fa) {
                        loading.value = false;
                        return;
                    }
                    if (res?.type === "web_temp_access" && res?.url !== null) {
                        let url = `${res.url}?member_token=${res.token}`;
                        if (activities.value) {
                            url = `${res.url}?member_token=${res.token}&activities=true`;
                        }
                        redirectUrl.value = res.url;
                        const resData = {};
                        for (const key in res) {
                            if (Object.prototype.hasOwnProperty.call(res, key) && key !== 'url') {
                                resData[key] = res[key];
                                localStorage.setItem(key, JSON.stringify(res[key]));
                            }
                        }
                        let resp = null;
                        let response = null;
                        try {
                            response = await fetch(url);
                            resp = await response.json();
                        } catch (error) {
                            const retryData = {
                                email: email.value,
                                password: password.value,
                                token: res.token,
                                merchant_id: merchant_id.value,
                                type: res.type,
                                corporate_id: corporate_id.value,
                                dashboard: dashboard.value,
                                ref: refParam.value
                            };
                            localStorage.setItem('autoLoginData', JSON.stringify(retryData));
                            validation.value = true;
                            passwordErrorMessage.value = "Auto-login failed. Please try again manually.";
                            return;
                        }
                        if (!response || !response.ok) {
                            await handleFetchError(response, resp, logout);
                            return;
                        }
                        handleWebTempAccess(resp, resData);
                        // Clean up SSO ref parameter from localStorage after successful login
                        localStorage.removeItem('sso_ref_param');
                        return;
                    }
                    if (res.level == "member") {
                        handleRoleRedirect();
                        localStorage.removeItem('sso_ref_param');
                        return;
                    }
                    // Non-member user: reject access on member portal
                    validationMemberOnly.value = true;
                    passwordErrorMessage.value = 'This login page is for members only. Please use the admin login page.';
                    localStorage.removeItem('sso_ref_param');
                    return;
                } catch (err) {
                    handleLoginErrorResponse(err);
                }
            });
        };
        const submitLogin = () => {
            login();
        };
        const logout = async (errorMessage = null) => {
            try {
                loading.value = true;
                let token = localStorage.getItem('token');
                if (token) {
                    token = token.replace(/^"(.*)"$/, '$1');
                    localStorage.removeItem('token');
                    localStorage.setItem('token', token);
                    let token2 = localStorage.getItem('token');
                    await store.dispatch('Auth/logoutError', token2);
                }
                localStorage.clear();
                if (errorMessage) {
                    validation.value = true;
                    passwordErrorMessage.value = errorMessage;
                }
            } catch (err) {
                loading.value = false;
            }
        };
        const checkSessionOnLoad = async () => {
            loading.value = true;
            setTimeout(() => {
                loading.value = false;
            }, 0);
        };
        const loginWithGoogle = async () => {
            const merchantId = route.query.merchant_id || null;
            const corporateId = route.query.corporate_id || null;
            const dashboardVal = route.query.dashboard || null;
            
            // Store ref parameter in localStorage before SSO redirect
            if (refParam.value) {
                localStorage.setItem('sso_ref_param', refParam.value);
            }
            
            // Get referral code from localStorage
            const referralCode = localStorage.getItem('campaign_referral_code') || referral_code.value || null;

            // Prepare state parameter with referral code
            let stateData = {};
            if (referralCode) {
                stateData.referral_code = referralCode;
            }

            // Encode state as base64
            const stateParam = btoa(JSON.stringify(stateData));

            if (merchantId) {
                store.dispatch('Auth/googleLogin', { merchant_id: merchantId, state: stateParam });
            } else if (corporateId && dashboardVal) {
                store.dispatch('Auth/googleLogin', { corporate_id: corporateId, dashboard: dashboardVal, state: stateParam });
            } else {
                // Fetch merchant_id from the public default-context endpoint
                try {
                    const response = await api.request('get', '/api/member/default-context');
                    const defaultMerchantId = response.data?.data?.merchant_id || null;
                    if (defaultMerchantId) {
                        store.dispatch('Auth/googleLogin', { merchant_id: defaultMerchantId, source: 'member_portal', state: stateParam });
                    } else {
                        store.dispatch('Auth/googleLogin', { source: 'member_portal', state: stateParam });
                    }
                } catch (error) {
                    store.dispatch('Auth/googleLogin', { state: stateParam });
                }
            }
        };

        // Native Google login handlers (GoogleLoginButton component emits these)
        const handleGoogleLoginSuccess = async (authData) => {
            try {
                loading.value = true;

                const userData = authData.data?.user || authData.user;
                const token = authData.data?.token || authData.token;
                const tenantDomain = authData.data?.tenant_domain || authData.tenant_domain;

                if (!token) throw new Error('No token received from backend');
                if (!userData) throw new Error('No user data received from backend');

                try {
                    localStorage.setItem('token', token);

                    // Normalize name fields for native
                    if (!userData.first_name && !userData.last_name && userData.name) {
                        const parts = userData.name.trim().split(' ');
                        userData.first_name = parts[0] || '';
                        userData.last_name = parts.slice(1).join(' ') || '';
                    }
                    if (!userData.title) userData.title = '';

                    localStorage.setItem('user', JSON.stringify(userData));
                    localStorage.setItem('level', userData.member ? 'member' : 'admin');
                    localStorage.setItem('member', JSON.stringify(userData.member || true));
                    localStorage.setItem('corporate_id', userData.corporate_id || '');

                    if (tenantDomain) {
                        localStorage.setItem('tenant_domain', tenantDomain);
                    }

                    await storageService.syncToNative();
                } catch (storageError) {
                    // Non-blocking
                }

                localStorage.removeItem('sso_ref_param');
                localStorage.removeItem('campaign_referral_code');

                store.commit('Auth/set_login_provider', 'google');
                store.commit('Auth/auth_success', { token, user: userData });
                await store.dispatch('Auth/auth_check');

                try {
                    await pushNotificationHandler.registerDevice();
                } catch (pushError) {
                    console.error('[MemberLogin] Push notification registration failed:', pushError);
                }

                router.replace({ name: 'client_dashboard' }).catch(() => {
                    window.location.href = '/client/dashboard';
                });

            } catch (error) {
                validation.value = true;
                passwordErrorMessage.value = error.message || 'Failed to complete Google login. Please try again.';
                loading.value = false;
            }
        };

        const googleLoginErrorMessages = {
            'AUTH.LOGIN.FAILED.MEMBER.NOT.YET.ACCEPTED': 'Your account is still pending approval. Please wait for merchant verification.',
            'AUTH.LOGIN.FAILED.MEMBER.LOGIN.WITH.SOCIALITE': 'This account was registered via email. Please log in using your email and password.',
            'AUTH.GOOGLE.EMAIL_EXISTS_APPLE': 'This email is already registered with Apple Sign-In. Please sign in using the Sign in with Apple button.',
            'AUTH.GOOGLE.ACCOUNT_MISMATCH': 'This email is already associated with a different Google account.',
        };

        const handleGoogleLoginError = (error) => {
            const code = error.code || '';
            const msg = error.message || '';
            passwordErrorMessage.value = googleLoginErrorMessages[code] || googleLoginErrorMessages[msg] || error.message || 'Google login failed. Please try again.';
            validation.value = true;
        };

        const handleAppleLoginSuccess = async (authData) => {
            try {
                loading.value = true;

                const userData = authData.data?.user || authData.user;
                const token = authData.data?.token || authData.token;
                const tenantDomain = authData.data?.tenant_domain || authData.tenant_domain;

                if (!token) throw new Error('No token received from backend for Apple login');
                if (!userData) throw new Error('No user data received from backend for Apple login');

                try {
                    localStorage.setItem('token', token);

                    if (!userData.first_name && !userData.last_name && userData.name) {
                        const parts = userData.name.trim().split(' ');
                        userData.first_name = parts[0] || '';
                        userData.last_name = parts.slice(1).join(' ') || '';
                    }
                    if (!userData.title) userData.title = '';

                    localStorage.setItem('user', JSON.stringify(userData));
                    localStorage.setItem('level', userData.member ? 'member' : 'admin');
                    localStorage.setItem('member', JSON.stringify(userData.member || true));
                    localStorage.setItem('corporate_id', userData.corporate_id || '');

                    if (tenantDomain) {
                        localStorage.setItem('tenant_domain', tenantDomain);
                    }

                    await storageService.syncToNative();
                } catch (storageError) {
                    // Non-blocking
                }

                localStorage.removeItem('sso_ref_param');
                localStorage.removeItem('campaign_referral_code');

                store.commit('Auth/set_login_provider', 'apple');
                store.commit('Auth/auth_success', { token, user: userData });
                await store.dispatch('Auth/auth_check');

                try {
                    await pushNotificationHandler.registerDevice();
                } catch (pushError) {
                    console.error('[MemberLogin] Apple push notification registration failed:', pushError);
                }

                router.replace({ name: 'client_dashboard' }).catch(() => {
                    window.location.href = '/client/dashboard';
                });

            } catch (error) {
                validation.value = true;
                passwordErrorMessage.value = error.message || 'Failed to complete Apple login. Please try again.';
                loading.value = false;
            }
        };

        const appleLoginErrorMessages = {
            'AUTH.LOGIN.FAILED.MEMBER.NOT.YET.ACCEPTED': 'Your account is still pending approval. Please wait for merchant verification.',
            'AUTH.APPLE.EMAIL_EXISTS_STANDARD': 'This email is already registered with Email & Password. Please sign in using the standard login form.',
            'AUTH.APPLE.EMAIL_EXISTS_GOOGLE': 'This email is already registered with Google Sign-In. Please sign in using the Sign in with Google button.',
            'AUTH.APPLE.ACCOUNT_MISMATCH': 'This email is already linked to a different Apple account.',
            'CANCELED': 'Apple Sign-In failed: Cancelled by user.',
        };

        const handleAppleLoginError = (error) => {
            const code = error.code || '';
            const msg = error.message || '';
            const lower = (code + ' ' + msg).toLowerCase();

            if (lower.includes('cancel') || lower.includes('1001') || lower.includes('1000')) {
                passwordErrorMessage.value = 'Apple Sign-In failed: Cancelled by user.';
            } else {
                passwordErrorMessage.value = appleLoginErrorMessages[code] || error.message || 'Apple Sign-In failed. Please try again.';
            }
            validation.value = true;
        };

        const loginWithFacebook = () => {
            const merchantId = route.query.merchant_id || null;
            store.dispatch('Auth/facebookLogin', merchantId);
        };
        const loginWithLinkedin = () => {
            const merchantId = route.query.merchant_id || null;
            store.dispatch('Auth/linkedinLogin', merchantId);
        };
        const performAuthCheck = async () => {
            if (_authCheckPromise.value) return _authCheckPromise.value;
            _authCheckPromise.value = store.dispatch('Auth/auth_check')
                .then((resp) => {
                    localStorage.setItem('id', resp.data.id);
                    if (resp.data.member) {
                        localStorage.setItem('level', 'member');
                    }
                    router.replace({ name: "client_dashboard" });
                    return resp;
                })
                .catch(() => {
                    // Do nothing, stay on current login page
                })
                .finally(() => {
                    setTimeout(() => {
                        _authCheckPromise.value = null;
                    }, 5000);
                });
            return _authCheckPromise.value;
        };
        const clearAutoLoginData = () => {
            localStorage.removeItem('autoLoginData');
            localStorage.removeItem('autoLoginRetryCount');
        };
        const checkImage = (url) => {
            return new Promise((resolve) => {
                const img = new window.Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = url;
            });
        };
        const handleStorageChange = (event) => {
            if (event.key === 'loginBackground') {
                loginBackground.value = event.newValue || '/img/temporary-login.png';
            }
        };

        // Check feature flag for new auth module
        const checkFeatureFlag = async () => {
            try {
                const response = await api.request('get', '/api/public/auth-module-status');
                const isEnabled = response.data?.data?.enabled;
                if (!isEnabled) {
                    // Native: redirect ke Login daripada nampilin 403 page
                    if (Capacitor.isNativePlatform()) {
                        router.replace({ name: 'Login' });
                        return;
                    }
                    featureFlagActive.value = false;
                }
            } catch (error) {
                // Native: flag check failed -> fallback ke Login
                if (Capacitor.isNativePlatform()) {
                    router.replace({ name: 'Login' });
                    return;
                }
                featureFlagActive.value = false;
            }
        };

        const handlePageShow = (event) => {
            if (event.persisted) {
                window.location.reload();
            }
        };

        // Lifecycle
        onMounted(async () => {
            window.addEventListener('storage', handleStorageChange);
            window.addEventListener('pageshow', handlePageShow);
            await setSwipeBackEnabled(false);
            loading.value = true;

            // Ensure session is restored before checking login state
            await store.dispatch('Auth/restoreSession');

            // Check if already logged in (from native storage restore)
            if (isLoggedIn.value) {
                const level = localStorage.getItem('level');
                
                // Check if we are accessed from Booking Engine/external integration
                const urlParams = new URLSearchParams(window.location.search);
                const merchantId = urlParams.get('merchant_id') || route.query.merchant_id;
                const corporateId = urlParams.get('corporate_id') || route.query.corporate_id;
                const dashboardVal = urlParams.get('dashboard') === 'true' || route.query.dashboard;
                const refParamVal = urlParams.get('ref') || route.query.ref || localStorage.getItem('sso_ref_param') || '';
                
                if (!isNativePlatform.value && (merchantId || (corporateId && dashboardVal))) {
                    const currUser = store.state.Auth.currUser;
                    const userEmail = currUser?.email || currUser?.realEmail || localStorage.getItem('email') || '';
                    
                    if (userEmail) {
                        const data = {
                            email: userEmail,
                            merchant_id: merchantId || "",
                            corporate_id: corporateId || "",
                            dashboard: dashboardVal || "",
                            otp_login: "login",
                            type: "web_temp_access",
                            ref: refParamVal || undefined
                        };
                        try {
                            const res = await store.dispatch('Auth/checkLogin', data);
                            const processed = await processOtpResponse(res);
                            if (processed) return;
                        } catch (err) {
                            console.error("Auto login to external integration failed", err);
                            handleLoginErrorResponse(err);
                            await logout(passwordErrorMessage.value || "Your current session is invalid for this merchant. Please log in again.");
                            return;
                        }
                    }
                }
                
                if (level === 'member') {
                    router.replace({ name: 'client_dashboard' });
                } else {
                    router.replace({ name: 'Home' });
                }
                return;
            }

            // Check feature flag first
            await checkFeatureFlag();
            if (!featureFlagActive.value) {
                loading.value = false;
                return;
            }
            try {
                const urlParams = new URLSearchParams(window.location.search);
                activities.value = urlParams.get('activities') === 'true' || route.query.activities;
                refParam.value = urlParams.get('ref') || route.query.ref || localStorage.getItem('sso_ref_param') || ''; // Extract ref parameter, fallback to localStorage for SSO
                const emailParam = urlParams.get('email');
                const passwordParam = urlParams.get('member_token');
                const token = urlParams.get('token');
                const merchantId = urlParams.get('merchant_id') || route.query.merchant_id;
                const type = urlParams.get('type');
                const errorMessage = urlParams.get('error_message');
                const corporateId = urlParams.get('corporate_id') || route.query.corporate_id;
                const dashboardVal = urlParams.get('dashboard') === 'true' || route.query.dashboard;
                const registerStateFromUrl = urlParams.get('registerState');
                corporate_id.value = corporateId;
                dashboard.value = dashboardVal;
                merchant_id.value = merchantId;

                // Capture referral code from URL and save to localStorage for Sign Up flow
                const referralFromUrl = urlParams.get('referral') || route.query.referral;
                if (referralFromUrl) {
                    referral_code.value = referralFromUrl;
                    localStorage.setItem('campaign_referral_code', referralFromUrl);
                } else {
                    // Check if referral code exists in localStorage
                    const storedReferral = localStorage.getItem('campaign_referral_code');
                    if (storedReferral) {
                        referral_code.value = storedReferral;
                    }
                }

                if (emailParam && passwordParam && token && type === 'web_temp_access') {
                    const loginData = { email: emailParam, password: passwordParam, token, merchant_id: merchantId, type, corporate_id: corporateId, dashboard: dashboardVal, ref: refParam.value };
                    localStorage.setItem('autoLoginData', JSON.stringify(loginData));
                }

                // Member portal SSO callback
                if (emailParam && passwordParam && token && type === 'member_portal') {
                    localStorage.setItem('token', token);
                    try {
                        await store.dispatch('Auth/auth_check');
                        const userLevel = store.state.Auth.currUser?.level;
                        if (userLevel) {
                            localStorage.setItem('level', userLevel);
                        }
                        router.replace({ name: 'client_dashboard' });
                    } catch (err) {
                        validation.value = true;
                        passwordErrorMessage.value = 'Login failed. Please try again manually.';
                    }
                    return;
                }

                await new Promise(resolve => setTimeout(resolve, 300));
                    if (isOtpMode.value) {
                    startCountdown();
                }
                if (errorMessage) {
                    validation.value = true;
                if (errorMessage.includes('Email+is+already')) {
                    passwordErrorMessage.value = 'Email is already registered. Please check your Google, Facebook, or LinkedIn account.';
                } else if (errorMessage.includes('Login+canceled')) {
                    passwordErrorMessage.value = 'Login with account canceled.';
                } else if (errorMessage.includes('No+user+found+with+this+google+login.')) {
                    passwordErrorMessage.value = 'No user found with this google login.';
                    urlParams.delete('error_message');
                    window.history.replaceState({}, '', window.location.pathname);
                    return;
                }
                passwordErrorMessage.value = decodeURIComponent(errorMessage.replace(/\+/g, ' '));
                handleLoginError(passwordErrorMessage.value);
                urlParams.delete('error_message');
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
                }
                if (merchantId) {
                    store.dispatch('Merchant/read_branch_logo', merchantId)
                        .then((resp) => {
                            branchLogo.value = resp;
                        })
                        .catch(err => {
                            console.error('Failed to load branch logo:', err);
                        });
                } else if (corporateId) {
                    store.dispatch('Corporate/read_corp_logo', corporateId)
                        .then((resp) => {
                            branchLogo.value = resp;
                        })
                        .catch(err => {
                            console.error('Failed to load corporate logo:', err);
                        });
                }
                const registerStateBefore = router.options?.history?.state?.register;
                registerState.value = registerStateBefore !== undefined;
                registerMessage.value = registerStateBefore && registerStateBefore != 0;
                const savedAutoLoginData = localStorage.getItem('autoLoginData');
                if (registerMessage.value === false) {
                    checkSessionOnLoad();
                } else if (emailParam && token && merchantId && passwordParam && type === 'web_temp_access') {
                    autoLoginData.value = { email: emailParam, password: passwordParam, token, merchant_id: merchantId, type };
                    await autoLogin();
                } else if (emailParam && token && passwordParam && dashboardVal && corporateId && !type && !merchantId) {
                    autoLoginData.value = { email: emailParam, password: passwordParam, token, corporate_id: corporateId, dashboard: dashboardVal };
                    await autoLogin();
                } else if (emailParam && token && passwordParam && !type && !merchantId) {
                    email.value = emailParam;
                    password.value = passwordParam;
                    await login();
                } else if (!emailParam && !passwordParam && merchantId && !type && !token) {
                    checkSessionOnLoad();
                } else if (!merchantId && localStorage.getItem('token')) {
                    const tokenType = localStorage.getItem('type');
                    if (tokenType === 'web_temp_access' || tokenType?.includes('web_temp_access')) {
                        await performAuthCheck();
                    }
                } else if (savedAutoLoginData) {
                    autoLoginData.value = JSON.parse(savedAutoLoginData);
                    await autoLogin();
                } else {
                    checkSessionOnLoad();
                }
                if (localStorage.getItem('token') && localStorage.getItem('token').includes('"')) {
                    for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        let value = localStorage.getItem(key);
                        if (value && value.includes('"')) {
                            try {
                                const parsed = JSON.parse(value);
                                localStorage.setItem(key, typeof parsed === 'string' ? parsed : JSON.stringify(parsed));
                            } catch (e) {
                                const newValue = value.replace(/"/g, '');
                                localStorage.setItem(key, newValue);
                            }
                        }
                    }
                    localStorage.setItem('selected_business_profile', JSON.stringify({
                        corporate_id: null,
                        branch_id: null,
                        merchant_id: null
                    }));
                    if (!merchantId) {
                        await performAuthCheck();
                    }
                }
                if (registerStateFromUrl === '0') {
                    registerState.value = true;
                    registerMessage.value = false;
                }
            } catch (error) {
                console.error("Initialization error:", error);
            } finally {
                loading.value = false;
            }
            api.request('get', `/api/public/corporate-id`)
                .then(async (response) => {
                const corporateId = response.data.data;
                const s3Url = process.env.VUE_APP_S3_BUCKET;
                const loginBackgroundExists = await checkImage(`${s3Url}user_assets/${corporateId}/loginBackground.png`);
                const extensions = ['png', 'jpg', 'jpeg', 'webp'];
                let corporateLogoExists = false;
                let corporateLogoUrl = '';
                for (const ext of extensions) {
                    const logoUrl = `${s3Url}user_assets/${corporateId}/corporateLogo.${ext}`;
                    corporateLogoExists = await checkImage(logoUrl);
                    if (corporateLogoExists) {
                    corporateLogoUrl = logoUrl;
                    break;
                    }
                }
                if (loginBackgroundExists) {
                    loginBackground.value = `${s3Url}user_assets/${corporateId}/loginBackground.png`;
                    localStorage.setItem('loginBackground', loginBackground.value);
                } else {
                    loginBackground.value = '/img/temporary-login.png';
                    localStorage.setItem('loginBackground', loginBackground.value);
                }
                if (corporateLogoExists) {
                    corporateLogo.value = corporateLogoUrl;
                    localStorage.setItem('corporateLogo', corporateLogo.value);
                } else {
                    corporateLogo.value = '/img/icons/Color_Alternate-1024x381.png';
                    localStorage.setItem('corporateLogo', corporateLogo.value);
                }
                })
                .catch(error => {
                    console.error('Failed request', error);
                    loginBackground.value = '/img/temporary-login.png';
                    corporateLogo.value = '/img/icons/Color_Alternate-1024x381.png';
                });
            });
            onBeforeUnmount(() => {
                if (countdownInterval.value) {
                    clearInterval(countdownInterval.value);
                }
                window.removeEventListener('storage', handleStorageChange);
                window.removeEventListener('pageshow', handlePageShow);
            });

            // Tambahkan handleLoginError agar tidak error
            const handleLoginError = (message) => {
                passwordErrorMessage.value = message;
                const merchantId = route.query.merchant_id;
                const newUrl = merchantId ? `?merchant_id=${merchantId}` : '?';
                window.history.replaceState({}, '', newUrl);
            };
            const openPrivacyPolicy = async () => {
                try {
                    await store.dispatch('Policies/get_public_privacy_policy');
                    activeTab.value = 'privacy';
                    showPrivacyModal.value = true;
                } catch (error) {
                    console.error('Failed to load privacy policy:', error);
                    activeTab.value = 'privacy';
                    showPrivacyModal.value = true;
                }
            };

            const goToSignUp = () => {
                localStorage.setItem('auth_origin', 'member');
                const ref = route.query.ref;
            if (ref) {
                localStorage.setItem('register_ref_param', ref);
            }

            // Mark that sign up originated from member login
            localStorage.setItem('member_login_origin', 'true');
            
            // Build destination with referral code if available
            const query = {};
            if (referral_code.value) {
                query.referral = referral_code.value;
            }

            const merchantIdValue = corporate_id.value && dashboard.value 
                ? corporate_id.value 
                : merchant_id.value;
            
            if (merchantIdValue) {
                router.push({
                    name: 'MemberSignUp',
                    params: { merchant_id: merchantIdValue },
                    query: Object.keys(query).length > 0 ? query : undefined
                });
            } else {
                // Fetch merchant_id from tenant context via API
                store.dispatch('Member/member_default_context')
                    .then((res) => {
                        const contextMerchantId = res.data.data.merchant_id;
                        if (contextMerchantId) {
                            router.push({
                                name: 'MemberSignUp',
                                params: { merchant_id: contextMerchantId },
                                query: Object.keys(query).length > 0 ? query : undefined
                            });
                        }
                    })
                    .catch((err) => {
                        console.error('Failed to get default context for sign up', err);
                    });
            }
        };

        const openTermsConditions = async () => {
                try {
                    await store.dispatch('Policies/get_public_terms_conditions');
                    activeTab.value = 'terms';
                    showPrivacyModal.value = true;
                } catch (error) {
                    console.error('Failed to load terms conditions:', error);
                    activeTab.value = 'terms';
                    showPrivacyModal.value = true;
                }
            };

            const closePrivacyModal = () => {
                showPrivacyModal.value = false;
            };
            // Expose to template
            return {
                email,
                password,
                loading,
                validation,
                validateLevel,
                validationEntity,
                merchant_id,
                validationMemberOnly,
                registerState,
                registerMessage,
                showPassword,
                branchLogo,
                autoLoginData,
                corporate_id,
                dashboard,
                otp,
                otpRequested,
                otpErrorMessage,
                redirectUrl,
                showCountdown,
                showRefreshButton,
                countdownMinutes,
                countdownSeconds,
                countdownInterval,
                isOtpMode,
                isProcessing,
                loginBackground,
                corporateLogo,
                passwordErrorMessage,
                activities,
                otpEmail,
                referral_code,
                goToForgotPassword,
                signUpLink,
                formattedTime,
                setCorporateLogo,
                backgroundStyle,
                setLoadingState,
                autoLogin,
                switchToOtpMode,
                switchToLoginMode,
                startCountdown,
                resendOTP,
                login,
                logout,
                requestOtp,
                submitOtp,
                submitLogin,
                checkSessionOnLoad,
                loginWithGoogle,
                loginWithFacebook,
                loginWithLinkedin,
                performAuthCheck,
                clearAutoLoginData,
                checkImage,
                handleStorageChange,
                isLoggedIn,
                authStatus,
                handleLoginError,
                currentYear,
                openPrivacyPolicy,
                openTermsConditions,
                showPrivacyModal,
                activeTab,
                closePrivacyModal,
                goToSignUp,
                featureFlagActive,
                isNativePlatform,
                isIosPlatform,
                handleGoogleLoginSuccess,
                handleGoogleLoginError,
                handleAppleLoginSuccess,
                handleAppleLoginError,
            };
        }
    };
</script>
<style scoped>
/* --- New Glassmorphism Styles --- */
.login-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 100vh;
    width: 100%;
    position: relative;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 20px 16px;
}
.login-wrapper::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
}
.glass-login-card {
    background: #ffffff;
    border-radius: 8px;
    padding: 2.5rem;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
    z-index: 1;
    display: flex;
    flex-direction: column;
    margin: auto 0;
}
.glass-input {
    width: 100%;
    background: #f8f9fa;
    border: 1px solid #ced4da;
    color: #333333;
    padding: 12px 12px 12px 40px;
    border-radius: 8px;
    outline: none;
    transition: all 0.3s ease;
}
.glass-input::placeholder {
    color: #6c757d;
}
.glass-input:focus {
    background: #ffffff;
    border-color: #DE5B53;
}
.custom-input-group {
    position: relative;
}
.input-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 14px;
}
.text-link {
    color: #DE5B53;
    text-decoration: none;
    transition: color 0.3s ease;
}
.text-link:hover {
    color: #c44e47;
}
.show-password-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    cursor: pointer;
}
.login-button {
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    background-color: #DE5B53;
    color: white;
    border: none;
    font-weight: 500;
    transition: background-color 0.3s ease;
}
.login-button:hover {
    background-color: #c44e47;
    transform: none;
}
.login-button:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
}
.login-footer {
    position: relative;
    margin-top: 20px;
    width: 100%;
    text-align: center;
    color: white;
    z-index: 0;
    font-size: 13px;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.8);
}
/* --- End New Glassmorphism Styles --- */

.form-login {
    margin-top: 1%;
}
.row-wrapper {
    display: flex;
    width: 100%;
    height: 100vh;
    /* background-image: url(${loginBackground}); */
}
#picture {
    align-items: center;
}
.col-8 {
    width: 66.66%;
    display: grid;
    align-items: center;
    position: relative;
    overflow: hidden;

}
.image {
    height: 50%;
    object-fit: contain;
    /* background-image: url('./../../../public/img/Login.png'); */
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    position: absolute;
    top: 25%;
    left: 0;
    right: 0;
    bottom: 0;
}
.feature-desc {
    block-size: 100%;
}
.col-4 {
    width: 33.33%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: white;
    height: 100vh;
    box-shadow: -10px 0 20px rgba(0, 0, 0, 0.1);
}
.card-body#login-card {
    display: grid;
    justify-content: center;
    font-size: small;
    height: 100%;
    margin: 20%;
    overflow: auto;
}
.logo-brand {
    justify-content: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}
#logo-brand {
    display: block;
    max-width: 60%;
    max-height: 80px;
    width: auto;
    object-fit: contain;
}
#logo-brand-omnih {
  display: block;
  width: 80%;
  max-height: 100px;
  object-fit: contain;
}
.separator {
    border: none;
    height: 1px;
    background-color: gray;
    width: 100%;
    margin-top: 5%;
    margin-bottom: 1%;
}
#login, .card-title {
    font-size: 14px;
    font-weight: 600;
    color: gray;
    display: flex;
    text-align: center;
    justify-content: center;
}
#login-container {
    display: flex;
    margin-top: 6%;
    height: 30%;
    margin-bottom: -10%;
}
#login-container-not-web-temp {
  display: flex;
  margin-top: -65%;
  height: 30%;
  
}
.simple-footer {
    margin-top: auto;
    text-align: center;
    color: #98a6ad;
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
}
small {
    display: inline-block;
    width: 100%;
    text-align: center;
}
#login-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.login-button-container {
  margin-top: auto;
  margin-bottom: 20px;
}

.login-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  width: fit-content;
  font-size: 16px;
  color: white;
  background: linear-gradient(135deg, #ff416c, #ff4b2b);
  border-radius: 8px;
  font-weight: bold;
  box-shadow: 0 4px 10px rgba(255, 65, 108, 0.3);
  transition: background 0.3s ease, transform 0.3s ease;
}

.login-button:hover {
  background: linear-gradient(135deg, #ff6a00, #ee0979);
  transform: scale(1.05);
  color: white;
}
.spinner-position {
    margin-top: 10px;
}

#google-login-container {
    margin-top: 20px;
}

.google-login-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    width: fit-content;
    background-color: white;
    border: 1px solid #ddd;
    border-radius: 5px;
    margin-bottom: 10px;
}

.google-login-text {
    margin-left: 10px;
    font-size: 14px;
}

.divider-with-text {
    margin: 20px 0;
    text-align: center;
    color: #808080;
}

.simple-footer {
    text-align: center;
    margin-top: 20px;
    font-size: 12px;
    color: #808080;
}
@media (max-width: 768px) {
    #login-container-not-web-temp {
        margin-top: 20px; /* Adjust this if needed to avoid overlapping other elements */
    }

    .login-button {
        width: 100%; /* Ensure the button fills the available width on small screens */
        max-width: 300px; /* Set a max width to prevent it from getting too large */
        margin: 0 auto; /* Center it horizontally */
    }
    .col-8 {
        display: none;
    }
    .col-4 {
        width: 100%;
        max-width: 100%;
    }
    .row-wrapper {
        justify-content: center;
    }
}
.custom-alert {
  position: relative;
  background: linear-gradient(135deg, #ff4b2b, #ff416c);
  color: white;
  padding: 15px 20px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(255, 65, 108, 0.5);
  animation: fadeInDown 0.6s ease-out;
  display: flex;
  align-items: center;
  margin-top: 20px;
}
.alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.alert-icon {
  font-size: 24px;
  margin-right: 15px;
  animation: pulse 2s infinite;
}
p {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
}
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.signup-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  max-width: 600px;
  padding: 2%;
  flex-wrap: nowrap;
}
.signup-container:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}
@media (max-width: 768px) {
  .signup-container {
    flex-direction: column;
    text-align: center;
  }
  .signup-button {
    margin-top: 15px;
    width: 100%;
  }
  .card-body#login-card {
    display: grid;
    justify-content: center;
    font-size: small;
    height: 80%;
    margin: 20%;
    overflow: scroll;
  }
  .google-login-button {
    margin-top: 6%;
  }
}
@media (max-width: 1448px) {
  .signup-container {
    flex-direction: column;
    text-align: center;
  }
  .signup-button {
    margin-top: 15px;
    width: 100%;
  }
}
.divider-with-text {
  display: flex;
  align-items: center;
  text-align: center;
}
.divider-with-text::before,
.divider-with-text::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #ccc;
    margin: 0 10px;
}
.divider-with-text span {
    font-size: 14px;
    color: #666;
}
.no-underline {
  text-decoration: none;
}
input:-webkit-autofill,
input:-webkit-autofill:hover, 
input:-webkit-autofill:focus, 
input:-webkit-autofill:active {
  transition: background-color 5000s ease-in-out 0s, box-shadow 5000s ease-in-out 0s !important;
  -webkit-text-fill-color: #333333 !important;
}

input:disabled {
  background-color: #e9ecef !important;
  color: #6c757d !important;
  -webkit-text-fill-color: #6c757d !important;
}
@media (max-width: 375px) {
  #login-card {
      width: 100%;
      margin: auto;
  }
  .col-4 {
      width: 100%;
      padding: 10px;
      box-sizing: border-box;
  }
  .row-wrapper {
      flex-direction: column;
  }
  body, html {
      overflow-y: auto;
      height: 100%;
      margin: 0;
      padding: 0;
  }
  #app {
      height: auto;
      min-height: 100vh;
      padding-top: 0;
      box-sizing: border-box;
  }
  .card-body#login-card {
      overflow: visible;
      max-height: none;
      margin-top: 0;
      padding: 0;
  }
  .d-flex {
      display: flex;
      justify-content: center;
      align-items: center;
  }
  .d-flex.justify-content-center {
      justify-content: center;
  }
  .google-login-button {
      width: 100%;
      margin-bottom: 10px;
      padding: 10px;
      font-size: 14px;
  }
  .google-login-text {
      margin-left: 10px;
  }
}
.google-login-button {
  display: flex;
  align-items: center;
  justify-content: center; 
  background-color: white;
  color: #5f6368;
  font-size: 15px;
  font-weight: 500;
  border: 1px solid #5f6368;
  border-radius: 24px;
  padding: 10px 20px;
  cursor: pointer;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 300px;
  margin: 10px 0;
  transition: box-shadow 0.3s ease;
}

/* Hover efek untuk meningkatkan interaksi */
.google-login-button:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

/* Ikon tetap diatur secara proporsional */
.google-icon {
  margin-right: 10px; /* Sesuaikan jarak antara ikon dan teks */
  height: 18px;
  width: 18px;
}

.otp-link {
  color: #ff416c; /* Warna teks putih */
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;
}

.otp-link:hover {
  text-decoration: none;
  color: #ff416c;
  
}

/* Policies modal tab styling - same as sidebar */
.policies-tabs {
  display: flex;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.tab-button {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  background-color: transparent;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background-color: #e9ecef;
  color: #495057;
}

.tab-button.active {
  color: #cb5b5b;
  background-color: #fff;
  border-bottom-color: #cb5b5b;
  font-weight: 600;
}

.modal-content {
  min-width: 500px !important;
}

.policy-content {
  padding-bottom: 1.5rem;
}

/* Policies Modal Responsive Styles */
.policies-modal-content {
  min-width: 500px;
}

/* Mobile Responsive for Policies Modal */
@media screen and (max-width: 768px) {
  .policies-modal-content {
    min-width: 90vw !important;
    max-width: 90vw !important;
    margin: 10px auto !important;
  }
  
  .policies-container {
    height: 60vh !important;
    max-height: 60vh !important;
    min-height: 300px !important;
  }
  
  .tab-content {
    height: calc(60vh - 60px) !important;
    max-height: calc(60vh - 60px) !important;
    padding: 1rem !important;
  }
  
  .tab-button {
    padding: 0.5rem 0.75rem !important;
    font-size: 0.85rem !important;
  }
}

@media screen and (max-width: 480px) {
  .policies-modal-content {
    min-width: 95vw !important;
    max-width: 95vw !important;
    margin: 5px auto !important;
  }
  
  .policies-container {
    height: 55vh !important;
    max-height: 55vh !important;
    min-height: 250px !important;
  }
  
  .tab-content {
    height: calc(55vh - 60px) !important;
    max-height: calc(55vh - 60px) !important;
    padding: 0.75rem !important;
  }
  
  .tab-button {
    padding: 0.4rem 0.5rem !important;
    font-size: 0.8rem !important;
  }
  
  .policies-tabs {
    flex-wrap: nowrap;
  }
}

@media screen and (max-width: 375px) {
  .policies-modal-content {
    min-width: 98vw !important;
    max-width: 98vw !important;
    margin: 2px auto !important;
  }
  
  .policies-container {
    height: 50vh !important;
    max-height: 50vh !important;
    min-height: 200px !important;
  }
  
  .tab-content {
    height: calc(50vh - 55px) !important;
    max-height: calc(50vh - 55px) !important;
    padding: 0.5rem !important;
  }
  
  .tab-button {
    padding: 0.35rem 0.4rem !important;
    font-size: 0.75rem !important;
  }
}

/* Modal backdrop responsive */
@media screen and (max-width: 768px) {
  .policies-modal .modal-dialog {
    margin: 10px !important;
    max-width: none !important;
  }
}

/* Feature Flag Disabled - 403 Page */
.feature-flag-disabled {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    width: 100%;
    background: #f5f5f5;
    padding: 20px;
}

.feature-flag-disabled__content {
    text-align: center;
    max-width: 480px;
    padding: 40px;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.feature-flag-disabled__icon {
    font-size: 72px;
    font-weight: 700;
    color: #DE5B53;
    margin-bottom: 16px;
    line-height: 1;
}

.feature-flag-disabled__title {
    font-size: 22px;
    font-weight: 600;
    color: #333333;
    margin-bottom: 12px;
}

.feature-flag-disabled__message {
    font-size: 14px;
    color: #6c757d;
    line-height: 1.6;
    margin: 0;
}
</style>